import { motion, AnimatePresence } from 'framer-motion';
import { Check, Lock, Pencil } from 'lucide-react';
import { cn } from '@/lib/utils';

interface FormSectionProps {
  stepNumber: number;
  title: string;
  description: string;
  isActive: boolean;
  isComplete: boolean;
  isLocked: boolean;
  summary?: React.ReactNode;
  onEdit: () => void;
  children: React.ReactNode;
}

export function FormSection({
  stepNumber,
  title,
  description,
  isActive,
  isComplete,
  isLocked,
  summary,
  onEdit,
  children
}: FormSectionProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: stepNumber * 0.1 }}
      className={cn(
        'rounded-xl border transition-all duration-300',
        isActive && 'border-gold/50 bg-card shadow-lg',
        isComplete && !isActive && 'border-border bg-card/50',
        isLocked && 'border-border bg-muted/30 opacity-60'
      )}
    >
      {/* Section Header */}
      <div
        className={cn(
          'flex items-start gap-4 p-4 sm:p-5',
          isComplete && !isActive && 'cursor-pointer hover:bg-muted/50 transition-colors rounded-xl'
        )}
        onClick={() => isComplete && !isActive && onEdit()}
      >
        <div className="flex items-start gap-3 flex-1">
          {/* Step Number Badge */}
          <div className={cn(
            'w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-bold',
            isComplete ? 'bg-gold text-dark-bg' : 'bg-muted text-muted-foreground'
          )}>
            {isComplete ? <Check className="w-4 h-4" /> : stepNumber}
          </div>

          <div className="flex-1 min-w-0">
            <h3 className={cn(
              'font-semibold text-base sm:text-lg',
              (isActive || isComplete) ? 'text-foreground' : 'text-muted-foreground'
            )}>
              {title}
            </h3>
            {!isActive && isComplete && summary && (
              <p className="text-sm text-muted-foreground mt-1 truncate">{summary}</p>
            )}
            {isActive && (
              <p className="text-sm text-muted-foreground mt-1">{description}</p>
            )}
          </div>
        </div>

        {/* Edit Button - shows when complete but not active */}
        {isComplete && !isActive && (
          <button
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            className="flex items-center gap-1.5 text-sm text-gold hover:text-gold-light transition-colors"
          >
            <Pencil className="w-3.5 h-3.5" />
            Edit
          </button>
        )}

        {/* Lock Icon - shows when locked */}
        {isLocked && (
          <Lock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
        )}
      </div>

      {/* Section Content - expandable */}
      <AnimatePresence>
        {isActive && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden"
          >
            <div className="px-4 sm:px-5 pb-5">
              {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
